import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { GameData } from "../Channels/Common/Datas/GameData";
import { DataManager } from "../Managers/DataManager";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { Symbol } from "../GameComponents/Symbol";
import { Symbols } from "../GameComponents/Symbols";
import { ModuleManager } from "../Managers/ModuleManager";
import { CustomUtils } from "../Utils/CustomUtils";
import { ReelExtended } from "./ReelExtended";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { WinAnimatorExtended } from "./WinAnimatorExtended";

/**
 * Machine handle reels and symbols for reels rotation.
 * Machine define behaviour for complete flow of reel rotation.
 * @class Machine
 * @extends BaseElement
 */

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    protected _spinnedReelsCounter: number = 0; // helper to count spinned reels
    protected _stoppingReelsCounter: number = 0; // helper to count stopping reels
    protected spinBitmask: number;

    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    //******************************************************************************************************************
    //  TIMERS
    //******************************************************************************************************************

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected _clonedReelIndex: number;

    protected _clonedReelCount: number;

    protected _skipStopDelayReelIndex: Array<number>;

    protected _reelIndexOffsetStopDelay: number;

    protected _teasing: ContainerElement;

    private _winAnimator: WinAnimatorExtended;

    protected _lastTeasing: string = "";
    protected _teasingSymbol: Array<number> = [0, 0, 0, 0, 0];
    private _fullStackedReel: Array<boolean> = [false, false, false, false, false];
    protected multiplierIndex: Array<number> = [0, 0, 0, 0, 0];
    private _canTease: Array<boolean> = [false, false, false, false, false];

    protected _forceStop: boolean;
    protected _wasRespin: boolean = false;
    protected _stackedSymbols: Array<Array<boolean>> = [
        [false, false, false],
        [false, false, false],
        [false, false, false],
        [false, false, false],
        [false, false, false],
    ];

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._clonedReelIndex = -1;
        this._clonedReelCount = -1;
        this._skipStopDelayReelIndex = [];
        this._reelIndexOffsetStopDelay = -1;
        this.spinBitmask = 0b11111;
    }

    protected createReels(
        reels: Array<Array<number>>,
        reelValues?: Array<Array<number>>,
        respinValues?: Array<Array<number>>,
    ): void {
        super.createReels(reels);

        if (reelValues != null && reelValues.length > 0) {
            let index = 0;
            for (let i: number = 0; i < this._reelCount; i++) {
                const reel = this._reels[i];

                //custom stopDelay settings
                if (this._skipStopDelayReelIndex.indexOf(i) == -1) {
                    if (this._reelIndexOffsetStopDelay > 0) {
                        reel.setStartDelay(this._startDelay * (index + this._reelIndexOffsetStopDelay));
                        reel.setStopDelay(this._stopDelay * (index + this._reelIndexOffsetStopDelay));
                        index++;
                    }
                }

                reel.setValuesStrip(reelValues[i]);
            }
        }

        if (respinValues != null && respinValues.length > 0) {
            for (let i: number = 0; i < this._reelCount; i++) {
                const reel = this._reels[i];
                reel.setRespinStrip(respinValues[i]);
            }
        }
    }

    protected configurate(node: Element): void {
        super.configurate(node);

        if (node.hasAttribute("ClonedReelIndex")) {
            this._clonedReelIndex = parseInt(node.getAttribute("ClonedReelIndex"));
        }

        if (node.hasAttribute("ClonedReelCount")) {
            this._clonedReelCount = parseInt(node.getAttribute("ClonedReelCount"));
        }

        if (node.hasAttribute("SkipStopDelayReelIndex")) {
            this._skipStopDelayReelIndex = ArrayUtils.numberArray(node.getAttribute("SkipStopDelayReelIndex"));
        }

        if (node.hasAttribute("ReelIndexOffsetStopDelay")) {
            this._reelIndexOffsetStopDelay = parseInt(node.getAttribute("ReelIndexOffsetStopDelay"));
        }
    }

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        reelValues?: Array<Array<number>>,
        teasing?: ContainerElement,
        reelsRespin?: Array<Array<number>>,
        winAnimator?: WinAnimatorExtended,
    ): void {
        this._symbols = symbols;
        this._teasing = teasing;
        this._winAnimator = winAnimator;

        reels = this.createClonedReels(reels);
        reelValues = this.createClonedReels(reelValues);

        this.createReels(reels, reelValues, reelsRespin);
    }

    //******************************************************************************************************************
    //  OVERRIDES
    //******************************************************************************************************************

    /**
     *
     * @param longRotation
     * @param useTransform
     * @param currentSymbols
     * @param spinBitmask
     * @return void
     */
    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
        spinBitmask: number = 0,
    ): void {
        this.spinBitmask = spinBitmask;

        if (this.spinBitmask !== 0) {
            if (DEBUG) {
                this.log("spinStart");
            }

            ReelExtended.Tease = -1;
            Machine.Respin = false;
            ReelExtended.TeaseingSymbols = [];

            if (!CustomUtils.isRespin()) {
                this._stackedSymbols = [
                    [false, false, false],
                    [false, false, false],
                    [false, false, false],
                    [false, false, false],
                    [false, false, false],
                ];
                this._fullStackedReel = [false, false, false, false, false];
                this._wasRespin = false;
                this._canTease = [false, false, false, false, false];
            } else {
                this._wasRespin = true;
            }

            this._stoppedFromServer = false;
            this._stoppedFromMachine = false;
            this._stoppedFromLegal = false;

            this._startSpinTime = Date.now();

            this._forceStop = false;

            this.visible = true;
            this._reelsContainer.visible = true;

            this.actualReelStopped = -1;
            this._stoppedReelIndexes = 0;
            this._spinnedReelsCounter = 0;
            this._stoppingReelsCounter = 0;

            if (this._reels != null) {
                for (let i = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if ((spinBitmask & (1 << i)) !== 0) {
                            if (this._stoppedSymbolsArray.length > 0) {
                                this._reels[i].visible = true;
                                this._reels[i].setData(this._stoppedSymbolsArray[i]);

                                //move current symbols from WinAnimator to Reel, not array reference
                                this._reels[i].currentSymbols = [];
                                for (let j = 0; j < this._visibleSymbolCount; j++) {
                                    this._reels[i].currentSymbols.push(currentSymbols[i][j]);
                                    currentSymbols[i][j] = null;
                                }

                                this._reels[i].setBaseRespinStrip(CustomUtils.isRespin());
                                this._reels[i].showSymbols(useTransform);
                                this._reels[i].spinStart();

                                this.setTeasing(i);
                                if (this._canTease[i] && this._spinnedReelsCounter === 0) {
                                    this.tease(i);
                                }

                                this._spinnedReelsCounter++;
                            } else {
                                if (DEBUG) {
                                    this.log("ERROR no stopped symbols");
                                }
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }

                if (longRotation != null && longRotation) {
                    this._stopTimer.start(this._minSpinRotationLong);
                } else {
                    this._stopTimer.start(this._minSpinRotation);
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }

            PlayList.play("SND_BaseRotationStart");
            PlayList.loop("SND_BaseRotation", 0, true);
        }
    }

    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (this.spinBitmask !== 0) {
            if (fromServer) {
                this._stoppedFromServer = true;
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                if (this.verifyMinGameRoundDurationConditions()) {
                    this._legalStopTimer = setTimeout(
                        () => this.spinStop(null, false, false, true),
                        this.getLegalRemainingSpinDuration(),
                    );
                } else {
                    this._stoppedFromLegal = true;
                }
            } else if (legal) {
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                this._stoppedFromLegal = true;
            } else {
                this._stopTimer.stop();
                this._stoppedFromMachine = true;
            }

            if (force) {
                this._forceStop = true;
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                this._stoppedFromLegal = true;
                this._stopTimer.stop();
                this._stoppedFromMachine = true;

                PlayList.stop("SND_BaseTeaser", 10);
            }

            if (DEBUG) {
                this.log("spinStop " + force);
            }

            if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
                if (
                    !ModuleManager.bonusActive() &&
                    !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
                ) {
                    DataManager.updateDataElement(GameData.DataTotalWin, "0");
                    DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
                }

                if (force && this._teasing != null && this._lastTeasing != "") {
                    const teasing: SwitchElement = this._teasing.childs[this._lastTeasing];
                    if (teasing) {
                        teasing.goToCase("STOPPING");
                    }
                    this._lastTeasing = "";
                }

                this._canTease = [false, false, false, false, false];
                if (this._wasRespin || CustomUtils.isRespin()) {
                    this.checkTeasing(force);
                }

                if (this._reels != null) {
                    let teasingDelay = 0;
                    let reelDelay = 0;
                    let customTeasingDelay = 130;
                    let firstReel = true;

                    for (let i: number = 0; i < this._reelCount; i++) {
                        if (this._reels[i] != null) {
                            if ((this.spinBitmask & (1 << i)) !== 0) {
                                // Stop only spinned reels.
                                if (this._stoppedSymbolsArray.length !== 0) {
                                    this._stoppingReelsCounter++;
                                    this._reels[i].setData(this._stoppedSymbolsArray[i]);

                                    if (
                                        force ||
                                        (!this._canTease[0] &&
                                            !this._canTease[1] &&
                                            !this._canTease[2] &&
                                            !this._canTease[3] &&
                                            !this._canTease[4])
                                    ) {
                                        this._reels[i].spinStop(force);
                                    } else {
                                        if (this._canTease[0]) {
                                            if (i === 0) {
                                                teasingDelay += customTeasingDelay;
                                                // ReelExtended.Tease = i;
                                                this._reels[i].spinTease([], teasingDelay);
                                            } else if (i === 1 || i === 2) {
                                                this._reels[i].spinStop(force);
                                            }
                                        } else if (this._canTease[1]) {
                                            if (i === 1) {
                                                teasingDelay += customTeasingDelay;
                                                // ReelExtended.Tease = i;
                                                this._reels[i].spinTease([], teasingDelay);
                                            } else if (i === 0 || i === 2) {
                                                reelDelay += this._stopDelay;
                                                this._reels[i].spinStop(force, reelDelay);
                                            }
                                        } else if (this._canTease[2]) {
                                            if (i === 2) {
                                                teasingDelay += customTeasingDelay;
                                                // ReelExtended.Tease = i;
                                                this._reels[i].spinTease([], teasingDelay);
                                            } else if (i === 0 || i === 1) {
                                                reelDelay += this._stopDelay;
                                                this._reels[i].spinStop(force, reelDelay);
                                            }
                                        }

                                        if (i === 3) {
                                            if (this._canTease[3]) {
                                                teasingDelay += customTeasingDelay;
                                                // ReelExtended.Tease = i;
                                                this._reels[i].spinTease([], teasingDelay);
                                            } else {
                                                reelDelay += this._stopDelay;
                                                this._reels[i].spinStop(force, teasingDelay + reelDelay);
                                            }
                                        }

                                        if (i === 4) {
                                            if (this._canTease[4]) {
                                                teasingDelay += customTeasingDelay;
                                                // ReelExtended.Tease = i;
                                                this._reels[i].spinTease([], teasingDelay);
                                            } else {
                                                reelDelay += this._stopDelay;
                                                this._reels[i].spinStop(force, teasingDelay + reelDelay);
                                            }
                                        }
                                    }
                                } else {
                                    if (DEBUG) {
                                        this.log("ERROR no stopped symbols");
                                    }
                                }
                                firstReel = false;
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no reel with index: " + i);
                            }
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reels");
                    }
                }
            }
        } else {
            this.emit(Machine.MachineStopped);
        }
    }

    protected checkTeasing(force: boolean): void {
        if (this._teasing == null) {
            return;
        }

        for (let i = 0; i < this._reelCount; i++) {
            this.setTeasing(i, force);

            if (!this._fullStackedReel[i]) {
                for (let j = 0; j < this._stoppedSymbolsArray[i].symbols.length; j++) {
                    if (this._stoppedSymbolsArray[i].overlay && this._stoppedSymbolsArray[i].overlay[j] >= 1) {
                        this._stackedSymbols[i][j] = true;
                    }
                }
                this._fullStackedReel[i] = this._stackedSymbols[i].indexOf(false) === -1;
            }
        }
    }

    protected setTeasing(i: number, force: boolean = false): void {
        switch (i) {
            case 0:
                if (this._fullStackedReel[1] && this._fullStackedReel[2] && this.stackSymbolsCountOnReel(i) === 2) {
                    this._canTease[0] = force ? false : true;
                }
                break;
            case 1:
                if (this._fullStackedReel[0] && this._fullStackedReel[2] && this.stackSymbolsCountOnReel(i) === 2) {
                    this._canTease[1] = force ? false : true;
                }

                break;
            case 2:
                if (this._fullStackedReel[0] && this._fullStackedReel[1] && this.stackSymbolsCountOnReel(i) === 2) {
                    this._canTease[2] = force ? false : true;
                }

                break;
            case 3:
                if (
                    this._fullStackedReel[0] &&
                    this._fullStackedReel[1] &&
                    this._fullStackedReel[2] &&
                    this.stackSymbolsCountOnReel(i) === 2
                ) {
                    this._canTease[3] = force ? false : true;
                }

                break;
            case 4:
                if (
                    this._fullStackedReel[0] &&
                    this._fullStackedReel[1] &&
                    this._fullStackedReel[2] &&
                    this._fullStackedReel[3] &&
                    this.stackSymbolsCountOnReel(i) === 2
                ) {
                    this._canTease[4] = force ? false : true;
                }

                break;
        }

        if (this._canTease[i]) {
            if (i <= 2) {
                if (this._fullStackedReel[3] && this._fullStackedReel[4]) {
                    this.multiplierIndex[i] = 5;
                } else if (this._fullStackedReel[3]) {
                    this.multiplierIndex[i] = 4;
                } else {
                    this.multiplierIndex[i] = 3;
                }
            } else if (i === 3) {
                if (this._fullStackedReel[4]) {
                    this.multiplierIndex[i] = 5;
                } else {
                    this.multiplierIndex[i] = 4;
                }
            } else {
                this.multiplierIndex[i] = 5;
            }

            this._teasingSymbol[i] = this._stackedSymbols[i].indexOf(false);
        }
    }

    protected stackSymbolsCountOnReel(reelIndex: number): number {
        let symbolCounter = 0;
        for (let i = 0; i < this._stackedSymbols[reelIndex].length; i++) {
            if (this._stackedSymbols[reelIndex][i]) {
                symbolCounter++;
            }
        }

        return symbolCounter;
    }

    public onReelStopped(reelIndex: number): void {
        if (!this._reels[reelIndex].isSkipped()) {
            PlayList.setPan("SND_BaseRotationEnd" + reelIndex, this._reelCount, reelIndex);
            PlayList.play("SND_BaseRotationEnd" + reelIndex);
        }

        if (this._wasRespin || CustomUtils.isRespin()) {
            this.tease(reelIndex, true);
        }

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;
        this.emit(Machine.ReelStopped, this._forceStop);

        if (this._stoppedReelIndexes === this._spinnedReelsCounter) {
            PlayList.stop("SND_BaseRotation", 10);
            this.stopTease();

            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }

            this._stopTimer.stop();

            this.emit(Machine.MachineStopped);
            this._reelsContainer.visible = false;
        }
    }

    protected tease(reelIndex: number, onReelStop: boolean = false): void {
        let index = reelIndex;
        if (onReelStop) {
            index = reelIndex + 1;
            if (reelIndex < this._reelCount - 1) {
                for (let i = reelIndex + 1; i < this._reelCount; i++) {
                    if ((this.spinBitmask & (1 << i)) !== 0) {
                        index = i;
                        break;
                    }
                }
            }
        }

        if (this._teasing != null && index < this._reelCount) {
            if (this._lastTeasing != "") {
                const teasingStop: SwitchElement = this._teasing.childs[this._lastTeasing];
                if (teasingStop) {
                    teasingStop.goToCase("STOPPING");
                    PlayList.stop("SND_BaseTeaser", 10);
                    PlayList.play("SND_BaseLoop", 10);
                }
                this._lastTeasing = "";
                ReelExtended.Tease = -1;
            }
            if (this._canTease[index]) {
                ReelExtended.Tease = index;
                this._lastTeasing = "Teasing_" + index + "_" + this._teasingSymbol[index];
                const teasing: SwitchElement = this._teasing.childs[this._lastTeasing];
                if (teasing) {
                    teasing.goToCase("START");
                }

                PlayList.play("SND_BaseTeaser");
                PlayList.stop("SND_BaseLoop");
                this._winAnimator.onSameSymbolsOnReelTeasingStart(this.multiplierIndex[index], index);
            }
        }
    }

    protected stopTease(): void {
        if (this._teasing) {
            const teasing: SwitchElement = this._teasing.childs[this._lastTeasing];
            if (teasing) {
                teasing.goToCase("STOPPING");
                PlayList.stop("SND_BaseTeaser", 10);
                PlayList.play("SND_BaseLoop", 10);
            }
            this._lastTeasing = "";
        }
        ReelExtended.Tease = -1;
    }

    private createClonedReels(reels: Array<Array<number>>): Array<Array<number>> {
        if (reels != null && this._clonedReelIndex > -1 && this._clonedReelCount > -1) {
            let reelToClone = reels[this._clonedReelIndex];

            if (reelToClone != null) {
                for (let i = 0; i < this._clonedReelCount - 1; i++) {
                    reels.splice(this._clonedReelIndex, 0, reelToClone);
                }
            }
        }

        return reels;
    }

    public addAdditionalReelSets(reelSets: any): void {
        for (let reelIndex: number = 0; reelIndex < this._reelCount; reelIndex++) {
            let reelSetsForReel: any = [];
            for (let i = 0; i < reelSets.length; i++) {
                reelSetsForReel.push({
                    id: reelSets[i].id,
                    reel: reelSets[i].reels[reelIndex],
                });
            }
            this._reels[reelIndex].addCustomData({ reelSets: reelSetsForReel });
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/